import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Button,
  Select,
  message,
  Spin,
  Card,
  Typography,
  Space,
  Alert,
  Upload,
  Row,
  Col
} from 'antd';
import {
  CloseOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { fetchWithCsrf } from '../utils/csrf';
import { mergeTemplateFieldValues, extractLeadFields, hasMeaningfulValue } from '../utils/fieldMapping';

const { Step } = Steps;
const { Option } = Select;
const { Title, Text } = Typography;

interface NOSDocumentWizardProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  lead: Lead | null;
}

const NOS_DOC_TEMPLATES = [
  { name: 'form_portabilidade_fixa', display_name: 'Formulário Portabilidade FIXA' },
  { name: 'form_portabilidade_movel', display_name: 'Formulário Portabilidade MÓVEL' },
    { name: 'contrato_adesao_nos', display_name: 'Contrato de Adesão NOS' }
];

// Fix EMAIL_DEFAULT type
type EmailStepType = {
  subject: string;
  body: string;
  morada: string;
  contacto: string;
  documentos: string[];
  notas: string;
};

const EMAIL_DEFAULT: EmailStepType = {
  subject: 'Documentos para assinatura e instruções',
  body: '',
  morada: '',
  contacto: '',
  documentos: [],
  notas: ''
};

const EMAIL_DOC_OPTIONS = [
  'Assinar o FA no TABLET',
  'Assinar PROC no TABLET',
  'Assinar PROC em PAPEL e anexar na ASSANA',
  'Assinar portabilidade móvel em PAPEL e anexar na ASSANA',
  'Assinar portabilidade fixa em PAPEL e anexar na ASSANA',
  'Anexar Cópia do cartão de cidadão do titular no TABLET',
  'Anexar cópia do cartão de cidadão em ASSANA'
];

const NOSDocumentWizard: React.FC<NOSDocumentWizardProps> = ({ open, onClose, onSave, lead }) => {
  const [step, setStep] = useState(0);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [allFields, setAllFields] = useState<Record<string, any[]>>({});
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, string>>({});
  const [generatedDocs, setGeneratedDocs] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [documentPages, setDocumentPages] = useState<Record<string, any[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [emailStep, setEmailStep] = useState<EmailStepType>({ ...EMAIL_DEFAULT });
  const [emailFiles, setEmailFiles] = useState<File[]>([]);
  const [emailPreview, setEmailPreview] = useState('');
  const [sending, setSending] = useState(false);
  const [availableDocs, setAvailableDocs] = useState<any[]>([]);
  const [form] = Form.useForm();

  // Load field values on open (merge — never wipe in-session / autofill done elsewhere)
  useEffect(() => {
    if (open && lead?.id) {
      apiService.getFieldValues(lead.id).then((res) => {
        if (res.success) {
          const fv = res.field_values || {};
          if (fv['Nome1#0'] === undefined) fv['Nome1#0'] = '';
          if (fv['Nome2#0'] === undefined) fv['Nome2#0'] = '';
          if (fv['Nome1#1'] === undefined) fv['Nome1#1'] = '';
          if (fv['Nome2#1'] === undefined) fv['Nome2#1'] = '';
          setFieldValues((prev) => ({ ...prev, ...fv }));
        }
      });
    }
  }, [open, lead]);

  // Autofill: saved + in-session + lead + cross-field related names
  useEffect(() => {
    if (!open || !lead || Object.keys(allFields).length === 0) return;
    const leadFields = extractLeadFields(lead);
    if (!leadFields) return;

    const nosTemplates = ['form_portabilidade_fixa', 'form_portabilidade_movel'];
    const hasNOSTemplate = selectedTemplates.some((template) => nosTemplates.includes(template));

    setFieldValues((prev) => {
      const flat = Object.values(allFields).flat();
      const merged = mergeTemplateFieldValues(flat, lead, prev);
      if (!hasNOSTemplate || !leadFields.name) return merged;
      const fullName = leadFields.name;
      const next = { ...merged };
      if (!hasMeaningfulValue(next['Nome1#0'])) next['Nome1#0'] = fullName.slice(0, 16);
      if (!hasMeaningfulValue(next['Nome2#0'])) next['Nome2#0'] = fullName.slice(16);
      if (!hasMeaningfulValue(next['Nome1#1'])) next['Nome1#1'] = fullName.slice(0, 16);
      if (!hasMeaningfulValue(next['Nome2#1'])) next['Nome2#1'] = fullName.slice(16);
      return next;
    });
  }, [open, lead?.id, lead?.name, lead?.dif, lead?.address, lead?.phone, lead?.email, lead?.city, lead?.postal_code, allFields, selectedTemplates]);

  // Autofill Nome1#0 and Nome1#1 on page change for NOS documents
  useEffect(() => {
    const currentTemplate = selectedTemplates[currentDocumentIndex];
    const nosTemplates = ['form_portabilidade_fixa', 'form_portabilidade_movel'];
    
    if (nosTemplates.includes(currentTemplate) && lead) {
      // Get the current page config
      const pages = documentPages[currentTemplate] || [];
      
      if (currentPage === 1) {
        // Autofill Nome1#0 and Nome2#0 if empty (Page 1)
        const fullName = lead.name || '';
        setFieldValues((prev) => {
          const updates: Record<string, any> = {};
          if (!hasMeaningfulValue(prev['Nome1#0'])) updates['Nome1#0'] = fullName.slice(0, 16);
          if (!hasMeaningfulValue(prev['Nome2#0'])) updates['Nome2#0'] = fullName.slice(16);
          // Autofill identification number for page 1
          if (!hasMeaningfulValue(prev['Num_Doc_Identificação_1']) && lead.dif) {
            updates['Num_Doc_Identificação_1'] = lead.dif;
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      }
      
      if (pages.length > 0 && currentPage === pages.length) {
        // Autofill Nome1#1 and Nome2#1 if empty (Page 3)
        const fullName = lead.name || '';
        setFieldValues((prev) => {
          const updates: Record<string, any> = {};
          if (!hasMeaningfulValue(prev['Nome1#1'])) updates['Nome1#1'] = fullName.slice(0, 16);
          if (!hasMeaningfulValue(prev['Nome2#1'])) updates['Nome2#1'] = fullName.slice(16);
          // Autofill identification number for page 3
          if (!hasMeaningfulValue(prev['Num_Doc_Identificação_1#1']) && lead.dif) {
            updates['Num_Doc_Identificação_1#1'] = lead.dif;
          }
          // For MÓVEL form, also fill NIF_1 and NIF_2
          if (!hasMeaningfulValue(prev['NIF_1']) && lead.dif) {
            updates['NIF_1'] = lead.dif;
          }
          if (!hasMeaningfulValue(prev['NIF_2']) && lead.dif) {
            updates['NIF_2'] = lead.dif;
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      }
    }
  }, [currentPage, currentDocumentIndex, selectedTemplates, documentPages, lead?.id, lead?.name, lead?.dif]);



  // Save on page change
  useEffect(() => {
    if (step === 1 && open && lead?.id) {
      const timeoutId = setTimeout(async () => {
        // Only save if there are field values to save
        if (Object.keys(fieldValues).length > 0) {
          await apiService.saveFieldValues(lead.id, fieldValues);
          if (selectedTemplates.length > 0) {
            const currentTemplate = selectedTemplates[currentDocumentIndex];
            await apiService.saveDocumentProgress(lead.id, currentTemplate, currentPage, false);
          }
        }
      }, 1000); // Debounce for 1 second
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentPage, step, open, lead?.id, fieldValues, selectedTemplates, currentDocumentIndex, currentPage]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSelectedTemplates([]);
      setCurrentDocumentIndex(0);
      setAllFields({});
      setFieldValues({});
      setPreviewData({});
      setGeneratedDocs([]);
      setError(null);
      setDocumentPages({});
      setCurrentPage(1);
      setEmailStep({ ...EMAIL_DEFAULT });
      setEmailFiles([]);
      setEmailPreview('');
      setSending(false);
    }
  }, [open]);

  // Fetch available documents for the lead when opening or entering email step
  useEffect(() => {
    if (open && lead?.id) {
      apiService.getLeadDocuments(lead.id).then(res => {
        if (res.success) {
          setAvailableDocs(res.documents || []);
        }
      });
    }
  }, [open, lead]);

  // Load NOS fields and pages
  const loadFields = async (templateNames: string[]): Promise<Record<string, any[]>> => {
    setLoading(true);
    try {
      const fieldsMap: Record<string, any[]> = {};
      for (const templateName of templateNames) {
        const data = await apiService.getVodafoneTemplateFields(templateName); // NOS uses same endpoint
        fieldsMap[templateName] = data;
      }
      setAllFields(fieldsMap);
      return fieldsMap;
    } catch (e) {
      setError('Erro ao carregar os campos do documento.');
      return {};
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentPages = async (
    templateNames: string[],
    fieldsMap: Record<string, any[]>
  ) => {
    setLoading(true);
    try {
      const pagesMap: Record<string, any[]> = {};
      for (const templateName of templateNames) {
        try {
          const response = await apiService.getDocumentPages(templateName);
          if (response.pages && response.pages.length > 0) {
            pagesMap[templateName] = response.pages;
          } else {
            // fallback: single page with all fields
            const fields = fieldsMap[templateName] || [];
            pagesMap[templateName] = [
              {
                page_number: 1,
                page_name: 'Campos',
                page_description: 'Todos os campos do documento',
                field_names: fields.map(f => f.name)
              }
            ];
          }
        } catch (e) {
          const fields = fieldsMap[templateName] || [];
          pagesMap[templateName] = [
            {
              page_number: 1,
              page_name: 'Campos',
              page_description: 'Todos os campos do documento',
              field_names: fields.map(f => f.name)
            }
          ];
        }
      }
      setDocumentPages(pagesMap);
    } catch (e) {
      setError('Erro ao carregar as páginas do documento.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (values: string[]) => {
    setSelectedTemplates(values);
    setAllFields({});
    setPreviewData({});
    setGeneratedDocs([]);
    // Do not reset fieldValues here, so autofill and loaded values persist
  };

  const handleNextStep = async () => {
    if (step === 0 && selectedTemplates.length > 0) {
      setLoading(true);
      const fieldsMap = await loadFields(selectedTemplates);
      await loadDocumentPages(selectedTemplates, fieldsMap);
      let savedFv: Record<string, any> = {};
      if (lead?.id) {
        try {
          const res = await apiService.getFieldValues(lead.id);
          if (res.success && res.field_values) savedFv = res.field_values;
        } catch {
          /* ignore */
        }
      }
      const flat = Object.values(fieldsMap).flat();
      setFieldValues((prev) => mergeTemplateFieldValues(flat, lead, { ...prev, ...savedFv }));
      setLoading(false);
      setStep(1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleFieldChange = (name: string, value: any) => {
    setFieldValues((prev) => {
      const newValues = { ...prev, [name]: value };
      // Autofill related fields
      Object.values(allFields).flat().forEach(field => {
        if (field.related_fields && field.related_fields.includes(name) && field.name !== name) {
          newValues[field.name] = value;
        }
      });
      return newValues;
    });
  };

  const getCurrentTemplate = () => selectedTemplates[currentDocumentIndex];
  const getCurrentFields = () => {
    const currentTemplate = getCurrentTemplate();
    return currentTemplate ? allFields[currentTemplate] || [] : [];
  };

  // Save when moving from Campos to next step
  const handleNextDocument = async () => {
    if (lead?.id && Object.keys(fieldValues).length > 0) {
      await apiService.saveFieldValues(lead.id, fieldValues);
      if (selectedTemplates.length > 0) {
        const currentTemplate = selectedTemplates[currentDocumentIndex];
        await apiService.saveDocumentProgress(lead.id, currentTemplate, currentPage, false);
      }
    }
    if (currentDocumentIndex < selectedTemplates.length - 1) {
      setCurrentDocumentIndex(currentDocumentIndex + 1);
      setCurrentPage(1);
    } else {
      setStep(2);
    }
  };

  const handlePreviousDocument = () => {
    if (currentDocumentIndex > 0) {
      setCurrentDocumentIndex(currentDocumentIndex - 1);
      setCurrentPage(1);
    } else {
      setStep(0);
    }
  };

  const handlePreview = async () => {
    if (selectedTemplates.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const previewData: Record<string, string> = {};
      for (const templateName of selectedTemplates) {
        const templateFieldValues: Record<string, any> = { ...fieldValues };
        const templateFields = allFields[templateName] || [];
        
        // Ensure all required fields are present (even if empty)
        templateFields.forEach(field => {
          if (field.required && !(field.name in templateFieldValues)) {
            templateFieldValues[field.name] = '';
          }
        });
        
        const pdfBase64 = await apiService.previewVodafoneDocument(templateName, templateFieldValues, lead?.id);
        previewData[templateName] = pdfBase64;
      }
      setPreviewData(previewData);
      setStep(3);
    } catch (e) {
      setError('Erro ao gerar pré-visualização.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    const currentTemplate = getCurrentTemplate();
    const currentPages = documentPages[currentTemplate] || [];
    if (currentPage < currentPages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleGenerate = async () => {
    if (!lead || selectedTemplates.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const newGeneratedDocs: any[] = [];
      for (const templateName of selectedTemplates) {
        
        // Special handling for Contrato de Adesão NOS - save to database instead of generating PDF
        if (templateName === 'contrato_adesao_nos') {
          const templateFieldValues: Record<string, any> = { ...fieldValues };
          const templateFields = allFields[templateName] || [];
          
          // Ensure all required fields are present (even if empty)
          templateFields.forEach(field => {
            if (field.required && !(field.name in templateFieldValues)) {
              templateFieldValues[field.name] = '';
            }
          });
          
          // Save to database instead of generating PDF
          // Check if there's a file upload
          const hasFileUpload = templateFieldValues.documentos_anexos && templateFieldValues.documentos_anexos instanceof File;
          
          let response;
          if (hasFileUpload) {
            // Use FormData for file upload
            const formData = new FormData();
            formData.append('lead_id', lead.id.toString());
            formData.append('documentos_anexos', templateFieldValues.documentos_anexos);
            
            // Add other form fields
            Object.keys(templateFieldValues).forEach(key => {
              if (key !== 'documentos_anexos' && templateFieldValues[key] !== null && templateFieldValues[key] !== undefined) {
                formData.append(key, templateFieldValues[key].toString());
              }
            });
            
            response = await fetchWithCsrf('/api/contrato-adesao-nos/save', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
              },
              body: formData
            });
          } else {
            // Use JSON for regular data
            response = await fetchWithCsrf('/api/contrato-adesao-nos/save', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
              },
              body: JSON.stringify({
                lead_id: lead.id,
                form_data: templateFieldValues
              })
            });
          }
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(`Failed to save Contrato de Adesão NOS: ${errorData.error || response.statusText}`);
          }
          
          const result = await response.json();
          newGeneratedDocs.push({
            id: result.record_id,
            document_type: templateName
          });
        } else {
          // Regular PDF generation for other templates
          const templateFieldValues: Record<string, any> = { ...fieldValues };
          const templateFields = allFields[templateName] || [];
          
          // Ensure all required fields are present (even if empty)
          templateFields.forEach(field => {
            if (field.required && !(field.name in templateFieldValues)) {
              templateFieldValues[field.name] = '';
            }
          });
          
          const result = await apiService.generateDocument(lead.id, templateName, templateFieldValues);
          newGeneratedDocs.push({
            id: result.document.id,
            document_type: templateName
          });
        }
      }
      setGeneratedDocs(newGeneratedDocs);
      
      // For Contrato de Adesão NOS, skip steps 3 and 4 and go directly to completion
      if (selectedTemplates.includes('contrato_adesao_nos')) {
        setStep(7); // Go directly to completion step
      } else {
        setStep(4); // Normal flow for other documents
      }
      
      if (onSave) onSave();
    } catch (e) {
      setError('Erro ao guardar documentos.');
    } finally {
      setLoading(false);
    }
  };

  // Email step logic
  const handleEmailFieldChange = (field: string, value: any) => {
    setEmailStep(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailDocChange = (values: string[]) => {
    setEmailStep(prev => ({ ...prev, documentos: values }));
  };

  const handleEmailFileChange = (info: any) => {
    // Always use the current fileList and extract originFileObj for each
    setEmailFiles(info.fileList.map((f: any) => f.originFileObj).filter(Boolean));
  };

  const buildEmailBody = () => {
    return `Login Comercial Tablet:\nambaptista \nCoimbra_02\n\nContacto do comercial responsável:\n916155950\n\nNome de cliente:\n${lead?.name || ''}\n\nMorada:\n${emailStep.morada}\n\nContacto Cliente:\n${emailStep.contacto}\n\nDocumentos a assinar:\n${(emailStep.documentos || []).join('\n')}\n\nNotas:\n${emailStep.notas}`;
  };

  const handleEmailPreview = () => {
    setEmailPreview(buildEmailBody());
    setStep(6);
  };

  // Helper to format bytes as MB/KB
  const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  };

  // Calculate total attachment size (uploaded files only)
  const getTotalAttachmentSize = () => {
    let total = 0;
    emailFiles.forEach(f => { if (f && f.size) total += f.size; });
    return total;
  };
  const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20 MB

  const handleSendEmail = async () => {
    const totalSize = getTotalAttachmentSize();
    if (totalSize > MAX_ATTACHMENT_SIZE) {
      message.error('O tamanho total dos anexos excede o limite permitido (20 MB). Remova alguns ficheiros e tente novamente.');
      return;
    }
    setSending(true);
    try {
      const res = await apiService.sendEmailWithAttachments({
        subject: emailStep.subject,
        body: buildEmailBody(),
        files: emailFiles
      });
      if (res.success) {
        message.success('E-mail enviado com sucesso!');
        setStep(7);
      } else {
        message.error(res.message || 'Falha ao enviar o e-mail.');
      }
    } catch (e) {
      message.error('Erro ao enviar o e-mail.');
    } finally {
      setSending(false);
    }
  };

  const getSteps = () => [
    { title: 'Seleção' },
    { title: 'Campos' },
    { title: 'Revisão' },
    { title: 'Pré-visualização' },
    { title: 'Guardado' },
    { title: 'Gerar E-mail' },
    { title: 'Confirmação' }
  ];

  const canGoToEmail = availableDocs.length > 0;

  // Get user data for autofill
  const { user } = useCognitoAuth();
  
  // Autofill function
  const getAutofillValue = (field: any) => {
    if (!field.autofill) return '';
    
    switch (field.autofill) {
      case 'user_name':
        return user?.username || '';
      case 'lead_name':
        return lead?.name || '';
      case 'lead_nif':
        return lead?.dif || '';
      case 'lead_telefone':
        return lead?.phone || '';
      case 'lead_email':
        return lead?.email || '';
      case 'lead_cidade':
        return lead?.city || '';
      case 'lead_codigo_postal_1':
        if (lead?.postal_code) {
          return lead.postal_code.split('-')[0] || '';
        }
        return '';
      case 'lead_codigo_postal_2':
        if (lead?.postal_code) {
          return lead.postal_code.split('-')[1] || '';
        }
        return '';
      default:
        return '';
    }
  };

  // Render Google Form Survey page with autofill functionality
  const renderGoogleFormSurveyPage = (pageFields: any[], currentPage: number, totalPages: number) => {
    // Special handling for postal code and localidade fields to be on the same line
    const postalCodeFields = pageFields.filter(f => f.name === 'codigo_postal_1' || f.name === 'codigo_postal_2');
    const localidadeField = pageFields.find(f => f.name === 'localidade');
    const otherFields = pageFields.filter(f => !['codigo_postal_1', 'codigo_postal_2', 'localidade'].includes(f.name));

    return (
      <Form form={form} layout="vertical">
        {otherFields.map((field, idx) => {
          // Handle autofill
          const autofillValue = getAutofillValue(field);
          if (autofillValue && !fieldValues[field.name]) {
            handleFieldChange(field.name, autofillValue);
          }

          // File upload for documentos_anexos field
          if (field.name === 'documentos_anexos') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Upload
                  beforeUpload={() => false}
                  onChange={(info) => {
                    if (info.fileList.length > 0) {
                      handleFieldChange(field.name, info.fileList[0].originFileObj);
                    } else {
                      handleFieldChange(field.name, null);
                    }
                  }}
                  fileList={fieldValues[field.name] ? [{
                    uid: '1',
                    name: fieldValues[field.name].name || 'documento.pdf',
                    status: 'done',
                    originFileObj: fieldValues[field.name]
                  }] : []}
                  accept=".pdf"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>
                    Selecionar PDF
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Apenas ficheiros PDF são permitidos
                </Text>
              </Form.Item>
            );
          }

          // Handle different field types
          if (field.type === 'select') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Select
                  value={fieldValues[field.name] || ''}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  placeholder={`Selecione ${field.label}`}
                  allowClear
                >
                  {field.options?.map((option: string) => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }

          if (field.type === 'textarea') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input.TextArea
                  value={fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder || `Introduza ${field.label}`}
                  rows={4}
                />
              </Form.Item>
            );
          }

          if (field.type === 'email') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input
                  type="email"
                  value={fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={`Introduza ${field.label}`}
                />
              </Form.Item>
            );
          }

          if (field.type === 'file_upload') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Upload
                  beforeUpload={() => false}
                  onChange={(info) => {
                    if (info.fileList.length > 0) {
                      handleFieldChange(field.name, info.fileList[0].originFileObj);
                    } else {
                      handleFieldChange(field.name, null);
                    }
                  }}
                  fileList={fieldValues[field.name] ? [{
                    uid: '1',
                    name: fieldValues[field.name].name || 'documento.pdf',
                    status: 'done',
                    originFileObj: fieldValues[field.name]
                  }] : []}
                  accept=".pdf"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>
                    Selecionar PDF
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Apenas ficheiros PDF são permitidos
                </Text>
              </Form.Item>
            );
          }

          // Handle validation for text fields
          if (field.validation) {
            const { pattern, message, maxLength } = field.validation;
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input
                  value={fieldValues[field.name] || ''}
                  maxLength={maxLength}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (pattern === '^[0-9]{9}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 9) val = val.slice(0, 9);
                    } else if (pattern === '^[0-9]{4}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 4) val = val.slice(0, 4);
                    } else if (pattern === '^[0-9]{3}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 3) val = val.slice(0, 3);
                    } else if (pattern === '^PT[0-9]{23}$') {
                      val = val.replace(/[^PT0-9]/g, '');
                      if (val.length > 25) val = val.slice(0, 25);
                    }
                    handleFieldChange(field.name, val);
                  }}
                  placeholder={`Introduza ${field.label}`}
                />
                {message && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {message}
                  </Text>
                )}
              </Form.Item>
            );
          }

          // Default text field
          return (
            <Form.Item key={field.name} label={field.label} required={field.required}>
              <Input
                value={fieldValues[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={`Introduza ${field.label}`}
              />
            </Form.Item>
          );
        })}
        
        {/* Special layout for postal code and localidade fields */}
        {(postalCodeFields.length > 0 || localidadeField) && (
          <Row gutter={16}>
            {postalCodeFields.map((field) => {
              const autofillValue = getAutofillValue(field);
              if (autofillValue && !fieldValues[field.name]) {
                handleFieldChange(field.name, autofillValue);
              }
              
              return (
                <Col span={8} key={field.name}>
                  <Form.Item label={field.label} required={field.required}>
                    <Input
                      value={fieldValues[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder || `Introduza ${field.label}`}
                      maxLength={field.name === 'codigo_postal_1' ? 4 : 3}
                    />
                  </Form.Item>
                </Col>
              );
            })}
            {localidadeField && (
              <Col span={8}>
                {(() => {
                  const autofillValue = getAutofillValue(localidadeField);
                  if (autofillValue && !fieldValues[localidadeField.name]) {
                    handleFieldChange(localidadeField.name, autofillValue);
                  }
                  
                  return (
                    <Form.Item label={localidadeField.label} required={localidadeField.required}>
                      <Input
                        value={fieldValues[localidadeField.name] || ''}
                        onChange={(e) => handleFieldChange(localidadeField.name, e.target.value)}
                        placeholder={`Introduza ${localidadeField.label}`}
                      />
                    </Form.Item>
                  );
                })()}
              </Col>
            )}
          </Row>
        )}
      </Form>
    );
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <div>
          <Title level={4}>Selecione os modelos de documento NOS</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Pode selecionar múltiplos documentos para gerar em lote
          </Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Escolha um ou mais documentos"
            onChange={handleTemplateSelect}
            value={selectedTemplates}
            maxTagCount="responsive"
          >
            {NOS_DOC_TEMPLATES.map((tpl) => (
              <Option key={tpl.name} value={tpl.name}>{tpl.display_name}</Option>
            ))}
          </Select>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousStep}>Anterior</Button>
            <Button type="primary" onClick={handleNextStep} disabled={selectedTemplates.length === 0}>
              Próximo
            </Button>
            {canGoToEmail && (
              <Button type="default" onClick={() => setStep(5)}>
                Ir para E-mail
              </Button>
            )}
          </Space>
        </div>
      );
    }
    if (step === 1) {
      const currentTemplate = getCurrentTemplate();
      const currentFields = getCurrentFields();
      const currentPages = documentPages[currentTemplate] || [];
      if (!currentPages || currentPages.length === 0) {
        return <Alert message="Erro: Não há páginas configuradas para este documento." type="error" showIcon />;
      }
      const currentPageConfig = currentPages.find(p => p.page_number === currentPage) || currentPages[0];
      const pageFields = currentPageConfig ? currentFields.filter(field => currentPageConfig.field_names.includes(field.name)) : currentFields;
      const totalPages = currentPages.length;
      return (
        <Form layout="vertical">
          <Title level={4}>
            Documento {currentDocumentIndex + 1} de {selectedTemplates.length}: {NOS_DOC_TEMPLATES.find(t => t.name === currentTemplate)?.display_name || currentTemplate}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Preencha os campos para o documento "{NOS_DOC_TEMPLATES.find(t => t.name === currentTemplate)?.display_name || currentTemplate}"
          </Text>
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Página {currentPage} de {totalPages}: {currentPageConfig?.page_name}</Text>
            {currentPageConfig?.page_description && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{currentPageConfig.page_description}</Text>
              </div>
            )}
          </div>
          {(() => {
            // Custom layout for Google Form Survey
            if (currentTemplate === 'google_form_survey') {
              return renderGoogleFormSurveyPage(pageFields, currentPage, currentPages.length);
            }
            // Custom layout for first page of both NOS documents
            if ((currentTemplate === 'form_portabilidade_fixa' || currentTemplate === 'form_portabilidade_movel') && currentPage === 1) {
              // Operator row fields
                                const operatorFieldNames = ['Operadora_atual'];
              const operatorFields = pageFields.filter(f => operatorFieldNames.includes(f.name));
              
              // Name fields - use correct field names from configuration
              let nome1Field: any, nome2Field: any;
              nome1Field = pageFields.find(f => f.name === 'Nome1#0');
              nome2Field = pageFields.find(f => f.name === 'Nome2#0');
              
              // All other fields except operator and name fields
              const otherFields = pageFields.filter(f => 
                !operatorFieldNames.includes(f.name) && 
                f.name !== 'Nome1#0' && 
                f.name !== 'Nome2#0'
              );
              
              // Compute combined value for input
              const nome1Value = fieldValues['Nome1#0'] || '';
              const nome2Value = fieldValues['Nome2#0'] || '';
              const nomeValue = nome1Value + nome2Value;
              
              return (
                <>
                  <div style={{ marginBottom: 4, fontWeight: 500, textAlign: 'left' }}>Indique o Operador Atual:</div>
                  <Row gutter={16} style={{ marginBottom: 0 }}>
                    {operatorFields.map((field, idx) => (
                      <Col span={8} key={field.name} style={{ display: 'flex', alignItems: 'center' }}>
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      </Col>
                    ))}
                  </Row>
                  {/* Nome ou denominação social below operator row */}
                  {(nome1Field && nome2Field) && (
                    <Form.Item label="Nome ou denominação social:" required={nome1Field?.required || nome2Field?.required}>
                      <Input
                        value={nomeValue}
                        maxLength={36}
                        onChange={e => {
                          const val = e.target.value;
                          handleFieldChange('Nome1#0', val.slice(0, 16));
                          handleFieldChange('Nome2#0', val.slice(16));
                        }}
                      />
                      {nomeValue.length >= 36 && (
                        <div style={{ color: 'orange', fontSize: 12 }}>Limite máximo de 36 caracteres atingido.</div>
                      )}
                    </Form.Item>
                  )}
                  {otherFields.map((field, idx) => {
                    // Handle validation rules for specific fields
                    if (field.validation) {
                      const { maxLength, pattern, message } = field.validation;
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            value={fieldValues[field.name] || ''}
                            maxLength={maxLength}
                            onChange={e => {
                              let val = e.target.value;
                              if (pattern === '^[0-9]*$') {
                                val = val.replace(/[^0-9]/g, '');
                                if (val.length > maxLength) val = val.slice(0, maxLength);
                              }
                              handleFieldChange(field.name, val);
                            }}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                          {message && (
                            <div style={{ color: '#1890ff', fontSize: 12, marginTop: 4 }}>
                              {message}
                            </div>
                          )}
                        </Form.Item>
                      );
                    }
                    
                    // Legacy handling for Num_Doc_Identificação_1 (backward compatibility)
                    if (field.name === 'Num_Doc_Identificação_1') {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            value={fieldValues[field.name] || ''}
                            maxLength={16}
                            onChange={e => {
                              let val = e.target.value.replace(/[^0-9]/g, '');
                              if (val.length > 16) val = val.slice(0, 16);
                              handleFieldChange(field.name, val);
                            }}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    }

                    if (field.type === 'number') {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            type="number"
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      </Form.Item>
                    );
                  })}
                </>
              );
            }
            // Custom layout for last page (page 3) of both NOS documents
            if ((currentTemplate === 'form_portabilidade_fixa' || currentTemplate === 'form_portabilidade_movel') && currentPages && currentPage === currentPages.length) {
              // Name fields - use correct field names from configuration
              let nome1Field: any, nome2Field: any;
              nome1Field = pageFields.find(f => f.name === 'Nome1#1');
              nome2Field = pageFields.find(f => f.name === 'Nome2#1');
              
              // Date fields
              const dateFieldNames = ['Dia', 'Mês', 'Ano'];
              const dateFields = pageFields.filter(f => dateFieldNames.includes(f.name));
              
              // All other fields except name and date fields
              const otherFields = pageFields.filter(f => 
                f.name !== 'Nome1#1' && f.name !== 'Nome2#1' && 
                !dateFieldNames.includes(f.name)
              );
              
              // Compute combined value for input
              const nome1Value = fieldValues['Nome1#1'] || '';
              const nome2Value = fieldValues['Nome2#1'] || '';
              const nomeValue = nome1Value + nome2Value;
              
              return (
                <>
                  {/* Nome ou denominação social at the top of the page if present */}
                  {nome1Field && nome2Field && (
                    <Form.Item label="Nome ou denominação social:" required={nome1Field?.required || nome2Field?.required}>
                      <Input
                        value={nomeValue}
                        onChange={e => {
                          const val = e.target.value;
                          handleFieldChange('Nome1#1', val.slice(0, 16));
                          handleFieldChange('Nome2#1', val.slice(16));
                        }}
                        placeholder="Introduza o nome ou denominação social"
                      />
                    </Form.Item>
                  )}
                  {otherFields.map((field, idx) => {
                    // Handle validation rules for specific fields
                    if (field.validation) {
                      const { maxLength, pattern, message } = field.validation;
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            value={fieldValues[field.name] || ''}
                            maxLength={maxLength}
                            onChange={e => {
                              let val = e.target.value;
                              if (pattern === '^[0-9]*$') {
                                val = val.replace(/[^0-9]/g, '');
                                if (val.length > maxLength) val = val.slice(0, maxLength);
                              }
                              handleFieldChange(field.name, val);
                            }}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                          {message && (
                            <div style={{ color: '#1890ff', fontSize: 12, marginTop: 4 }}>
                              {message}
                            </div>
                          )}
                        </Form.Item>
                      );
                    }
                    
                    // Legacy handling for Num_Doc_Identificação_1#1 (backward compatibility)
                    if (field.name === 'Num_Doc_Identificação_1#1') {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            value={fieldValues[field.name] || ''}
                            maxLength={16}
                            onChange={e => {
                              let val = e.target.value.replace(/[^0-9]/g, '');
                              if (val.length > 16) val = val.slice(0, 16);
                              handleFieldChange(field.name, val);
                            }}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    }

                    if (field.type === 'number') {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            type="number"
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      </Form.Item>
                    );
                  })}
                  {/* Date fields in a row at the end */}
                  <Row gutter={16} style={{ marginBottom: 0 }}>
                    {dateFields.map((field, idx) => {
                      let min: number = 0, max: number = 0, maxLength: number = 0;
                      if (field.name === 'Dia') { min = 1; max = 31; maxLength = 2; }
                      else if (field.name === 'Mês') { min = 1; max = 12; maxLength = 2; }
                      else if (field.name === 'Ano') { min = 0; max = 9999; maxLength = 4; }
                      return (
                        <Col span={8} key={field.name} style={{ display: 'flex', alignItems: 'center' }}>
                          <Form.Item label={field.label || field.name} required={field.required} style={{ width: '100%' }}>
                            <Input
                              type="number"
                              min={min}
                              max={max}
                              maxLength={maxLength}
                              value={fieldValues[field.name] || ''}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={`Introduza ${field.label || field.name}`}
                            />
                          </Form.Item>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              );
            }
            // Custom layout for second page of both NOS documents
            if ((currentTemplate === 'form_portabilidade_fixa' || currentTemplate === 'form_portabilidade_movel') && currentPage === 2) {
              const dateFieldNames = ['Dia Esp', 'Mês Esp', 'Ano Esp'];
              const dateFields = pageFields.filter(f => dateFieldNames.includes(f.name));
              const otherFields = pageFields.filter(f => !dateFieldNames.includes(f.name));
              return (
                <>
                  {otherFields.map((field, idx) => {

                    if (field.name === 'Nº_Tlf_Portar') {
                      const telValue = fieldValues[field.name] || '';
                      const showWarning = telValue.startsWith('2') || telValue.length !== 9;
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            value={telValue}
                            maxLength={9}
                            onChange={e => {
                              let val = e.target.value.replace(/[^0-9]/g, '');
                              if (val.length > 9) val = val.slice(0, 9);
                              handleFieldChange(field.name, val);
                            }}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                          {telValue && showWarning && (
                            <div style={{ color: 'orange', fontSize: 12, marginTop: 4 }}>
                              Exclua o 2 inicial do número do fixo! Inclua os 9 dígitos do número
                            </div>
                          )}
                        </Form.Item>
                      );
                    }
                    if (field.type === 'number') {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Input
                            type="number"
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      </Form.Item>
                    );
                  })}
                  {/* Date fields in a row at the end */}
                  <div style={{ marginBottom: 4, fontWeight: 500, textAlign: 'left' }}>Indique a data específica para a realização da Portabilidade:</div>
                  <Row gutter={16} style={{ marginBottom: 0 }}>
                    {dateFields.map((field, idx) => {
                      let min: number = 0, max: number = 0, maxLength: number = 0;
                      if (field.name === 'Dia Esp') { min = 1; max = 31; maxLength = 2; }
                      else if (field.name === 'Mês Esp') { min = 1; max = 12; maxLength = 2; }
                      else if (field.name === 'Ano Esp') { min = 0; max = 9999; maxLength = 4; }
                      return (
                        <Col span={8} key={field.name} style={{ display: 'flex', alignItems: 'center' }}>
                          <Form.Item label={field.label ? field.label.replace(/Especial/i, '').trim() : field.name.replace(/Esp/i, '').trim()} required={field.required} style={{ width: '100%' }}>
                            <Input
                              type="number"
                              value={fieldValues[field.name] || ''}
                              min={min}
                              max={max}
                              maxLength={maxLength}
                              onChange={e => {
                                let val = e.target.value.replace(/[^0-9]/g, '');
                                if (val.length > maxLength) val = val.slice(0, maxLength);
                                if (val && ((min !== 0 && parseInt(val) < min) || (max !== 0 && parseInt(val) > max))) return;
                                handleFieldChange(field.name, val);
                              }}
                              placeholder={`Introduza ${field.label ? field.label.replace(/Especial/i, '').trim() : field.name.replace(/Esp/i, '').trim()}`}
                            />
                          </Form.Item>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              );
            }
            // Default rendering for all other cases
            return pageFields.map((field, idx) => {
              if (field.type === 'number') {
                return (
                  <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                    <Input
                      type="number"
                      value={fieldValues[field.name] || ''}
                      onChange={e => handleFieldChange(field.name, e.target.value)}
                      placeholder={`Introduza ${field.label || field.name}`}
                    />
                  </Form.Item>
                );
              }
              return (
                <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                  <Input
                    value={fieldValues[field.name] || ''}
                    onChange={e => handleFieldChange(field.name, e.target.value)}
                    placeholder={`Introduza ${field.label || field.name}`}
                  />
                </Form.Item>
              );
            });
          })()}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousDocument}>{currentDocumentIndex === 0 ? 'Voltar' : 'Anterior'}</Button>
            <Button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Página Anterior</Button>
            <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Próxima Página</Button>
            <Button type="primary" onClick={handleNextDocument}>{currentDocumentIndex === selectedTemplates.length - 1 ? 'Concluir' : 'Próximo'}</Button>
          </Space>
        </Form>
      );
    }
    if (step === 2) {
      return (
        <div>
          <Title level={4}>Revisão dos Documentos</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Revise os documentos selecionados antes de gerar
          </Text>
          {selectedTemplates.map((templateName, index) => {
            const templateObj = NOS_DOC_TEMPLATES.find(t => t.name === templateName);
            const templateFields = allFields[templateName] || [];
            const filledFields = templateFields.filter(field => {
              const value = fieldValues[field.name];
              return value !== undefined && value !== null && value !== '';
            });
            
            // Helper function to get display name for field
            const getFieldDisplayName = (fieldName: string) => {
              if (templateName === 'form_portabilidade_fixa') {
                if (fieldName === 'Nome1#0' || fieldName === 'Nome1#1') return 'Nome ou denominação social (Parte 1)';
                if (fieldName === 'Nome2#0' || fieldName === 'Nome2#1') return 'Nome ou denominação social (Parte 2)';
              }
              return fieldName;
            };
            
            // Helper function to get display value for field
            const getFieldDisplayValue = (fieldName: string, value: any) => {
              if (templateName === 'form_portabilidade_fixa') {
                if (fieldName === 'Nome1#0' && fieldValues['Nome2#0']) {
                  return `${value}${fieldValues['Nome2#0']}`;
                }
                if (fieldName === 'Nome1#1' && fieldValues['Nome2#1']) {
                  return `${value}${fieldValues['Nome2#1']}`;
                }
                // Don't show Nome2#0 or Nome2#1 separately if they're part of a combined name
                if (fieldName === 'Nome2#0' && fieldValues['Nome1#0']) return null;
                if (fieldName === 'Nome2#1' && fieldValues['Nome1#1']) return null;
              }
              return value;
            };
            
            // Filter out fields that shouldn't be shown separately
            const displayFields = filledFields.filter(field => {
              const displayValue = getFieldDisplayValue(field.name, fieldValues[field.name]);
              return displayValue !== null;
            });
            
            return (
              <Card key={templateName} style={{ marginBottom: 16 }}>
                <Title level={5}>Documento {index + 1}: {templateObj?.display_name || templateName}</Title>
                <Text type="secondary">
                  {displayFields.length} de {templateFields.length} campos preenchidos
                </Text>
                {displayFields.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Campos preenchidos:</Text>
                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                      {displayFields.slice(0, 5).map(field => {
                        const displayName = getFieldDisplayName(field.name);
                        const displayValue = getFieldDisplayValue(field.name, fieldValues[field.name]);
                        return (
                          <li key={field.name}>
                            <Text type="secondary">
                              {displayName}: {displayValue}
                            </Text>
                          </li>
                        );
                      })}
                      {displayFields.length > 5 && (
                        <li>
                          <Text type="secondary">... e mais {displayFields.length - 5} campos</Text>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>Voltar</Button>
            {selectedTemplates.includes('contrato_adesao_nos') ? (
              <>
                {currentPage > 1 && (
                  <Button onClick={handlePrevPage} disabled={loading}>
                    Página Anterior
                  </Button>
                )}
                {currentPage === (documentPages[getCurrentTemplate()] || []).length ? (
                  <Button type="primary" onClick={handleGenerate} disabled={loading}>
                    {loading ? <Spin size="small" /> : 'Concluído'}
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleNextPage} disabled={loading}>
                    Próxima Página
                  </Button>
                )}
              </>
            ) : (
              <Button type="primary" onClick={handlePreview} disabled={loading}>
                {loading ? <Spin size="small" /> : 'Pré-visualizar'}
              </Button>
            )}
          </Space>
        </div>
      );
    }
    if (step === 3) {
      return (
        <div>
          <Alert

            type="warning"
            showIcon
            style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}
          />
          <Title level={4}>Pré-visualização do PDF</Title>
          {Object.keys(previewData).length === 0 ? (
            <Spin />
          ) : (
            <div>
              {Object.entries(previewData).map(([templateName, pdfBase64]) => (
                <div key={templateName} style={{ marginBottom: 20 }}>
                  <Title level={5}>{NOS_DOC_TEMPLATES.find(t => t.name === templateName)?.display_name || templateName}</Title>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}>
                    <iframe
                      src={`data:application/pdf;base64,${pdfBase64}`}
                      title={`Pré-visualização PDF - ${templateName}`}
                      width="100%"
                      height="500px"
                      style={{ border: 'none' }}
                    />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      onClick={() => {
                        const blob = new Blob([Uint8Array.from(atob(pdfBase64), c => c.charCodeAt(0))], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${templateName}.pdf`;
                        link.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Descarregar PDF para verificar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(2)}>Voltar</Button>
            <Button type="primary" onClick={handleGenerate} disabled={loading}>
              Guardar Documentos
            </Button>
          </Space>
        </div>
      );
    }
    if (step === 4 && generatedDocs.length > 0) {
      return (
        <div>
          <Alert
            message={`${generatedDocs.length} documento(s) guardado(s) com sucesso!`}
            description="Os documentos foram guardados e estão agora disponíveis na secção 'Documentos' para este lead. Pode anexá-los ao e-mail ou fechar este assistente."
            type="success"
            showIcon
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(3)}>Voltar</Button>
            <Button type="primary" onClick={() => setStep(5)}>
              Gerar E-mail
            </Button>
          </Space>
        </div>
      );
    }
    if (step === 5) {
      return (
        <Form layout="vertical">
          <Title level={4}>Personalizar E-mail</Title>
          <Form.Item label="Assunto">
            <Input
              value={emailStep.subject}
              onChange={e => handleEmailFieldChange('subject', e.target.value)}
              placeholder="Assunto do e-mail"
            />
          </Form.Item>
          <Form.Item label="Morada do Cliente">
            <Input
              value={emailStep.morada}
              onChange={e => handleEmailFieldChange('morada', e.target.value)}
              placeholder="Morada do cliente"
            />
          </Form.Item>
          <Form.Item label="Contacto Cliente">
            <Input
              value={emailStep.contacto}
              onChange={e => handleEmailFieldChange('contacto', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Contacto (apenas números)"
              maxLength={15}
            />
          </Form.Item>
          <Form.Item label="Documentos a assinar">
            <Select
              mode="multiple"
              value={emailStep.documentos}
              onChange={handleEmailDocChange}
              placeholder="Selecione os documentos"
            >
              {EMAIL_DOC_OPTIONS.map(opt => (
                <Option key={opt} value={opt}>{opt}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Notas">
            <Input.TextArea
              value={emailStep.notas}
              onChange={e => handleEmailFieldChange('notas', e.target.value)}
              placeholder="Notas adicionais"
              rows={3}
            />
          </Form.Item>

          <Form.Item label="Anexar ficheiros">
            <Upload
              multiple
              beforeUpload={() => false}
              onChange={handleEmailFileChange}
              fileList={emailFiles.map(f => ({ uid: f.name, name: f.name, status: 'done', size: f.size }))}
              onRemove={file => {
                setEmailFiles(files => files.filter(f => f.name !== file.name));
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>Selecionar ficheiros</Button>
            </Upload>
            <Text type="secondary">Pode anexar ficheiros do seu PC.</Text>
            <ul style={{ marginTop: 8 }}>
              {emailFiles.map((file, idx) => (
                <li key={file.name || idx}>{file.name} <Text type="secondary">({formatBytes(file.size)})</Text>{!file.lastModified && <Text type="secondary"> (não será restaurado após sair)</Text>}</li>
              ))}
            </ul>
          </Form.Item>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(4)}>Voltar</Button>
            <Button type="primary" htmlType="button" onClick={e => { handleEmailPreview(); }}>
              Pré-visualizar E-mail
            </Button>
          </Space>
        </Form>
      );
    }
    if (step === 6) {
      return (
        <div>
          <Title level={4}>Pré-visualização do E-mail</Title>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 6, minHeight: 200 }}>{emailPreview}</pre>
          <div style={{ marginTop: 16 }}>
            <Text strong>Ficheiros anexados:</Text>
            <ul>
              {emailFiles.map((file, idx) => (
                <li key={file.name || idx}>{file.name} <Text type="secondary">({formatBytes(file.size)})</Text></li>
              ))}
              {emailFiles.length === 0 && (
                <li><Text type="secondary">Nenhum ficheiro anexado</Text></li>
              )}
            </ul>
          </div>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(5)}>Voltar</Button>
            <Button type="primary" loading={sending} onClick={handleSendEmail}>
              Confirmar e Enviar
            </Button>
          </Space>
        </div>
      );
    }
    if (step === 7) {
      // Check if this is Contrato de Adesão NOS completion
      const isContratoAdesaoNOS = selectedTemplates.includes('contrato_adesao_nos');
      
      return (
        <div>
          <Alert
            message={isContratoAdesaoNOS ? "Contrato de Adesão NOS guardado com sucesso!" : "E-mail enviado com sucesso!"}
            description={isContratoAdesaoNOS ? 
              "Os dados do Contrato de Adesão NOS foram guardados na base de dados." : 
              "O e-mail foi enviado com sucesso."
            }
            type="success"
            showIcon
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose} icon={<CloseOutlined />}>Fechar</Button>
          </Space>
        </div>
      );
    }
    return null;
  };

  // Key for localStorage
  const EMAIL_DRAFT_KEY = lead ? `nos_email_draft_lead_${lead.id}` : 'nos_email_draft';

  // Save email step info to localStorage whenever it changes
  useEffect(() => {
    if (!open || !lead) return;
    const draft = {
      emailStep,
      emailFilesMeta: emailFiles.map(f => ({ name: f.name, size: f.size, lastModified: f.lastModified })), // Only meta, not file data
    };
    localStorage.setItem(EMAIL_DRAFT_KEY, JSON.stringify(draft));
  }, [emailStep, emailFiles, open, lead]);

  // Restore email step info from localStorage when modal opens
  useEffect(() => {
    if (open && lead) {
      const draftRaw = localStorage.getItem(EMAIL_DRAFT_KEY);
      if (draftRaw) {
        try {
          const draft = JSON.parse(draftRaw);
          if (draft.emailStep) setEmailStep(draft.emailStep);
          // Note: We cannot restore actual File objects, so just show meta in UI
        } catch {}
      }
    }
  }, [open, lead]);

  // Clear draft on successful send or modal close
  useEffect(() => {
    if (!open && lead) {
      localStorage.removeItem(EMAIL_DRAFT_KEY);
    }
    if (step === 7 && lead) {
      localStorage.removeItem(EMAIL_DRAFT_KEY);
    }
  }, [open, step, lead]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Guardar Documentos NOS"
      width={700}
      destroyOnClose
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Steps 
        current={step} 
        style={{ marginBottom: 24 }}
        items={getSteps().map((stepItem, index) => ({
          key: index,
          title: stepItem.title
        }))}
      />
      {loading ? <Spin /> : renderStepContent()}
    </Modal>
  );
};

export default NOSDocumentWizard; 